/*
 * File: useSearchParamState.ts
 * Author: Austin Robinson
 * Date Created: 6/19/2025
 * Copyright (c) DrivingDoc 2025
 */
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { useSearchParams } from 'react-router';

type Serialize<T> = (value: T) => string;
type Deserialize<T> = (value: string) => T;

interface UseSearchParamStateOptions<T> {
  paramName: string;
  initialState: T;
  serialize: Serialize<T>;
  deserialize: Deserialize<T>;
  keepHistory?: boolean;
}

export function useSearchParamState<T>(options: UseSearchParamStateOptions<T>): [T, Dispatch<SetStateAction<T>>] {
  const { paramName, initialState, serialize, deserialize, keepHistory = false } = options;

  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState(() => {
    // get the current search param value
    const searchParam = searchParams.get(paramName);

    // if the search parameter is not null
    if (searchParam !== null) {
      // initialize state with the deserialized value
      return deserialize(searchParam);
    }

    // initialize state with the initial value
    return initialState;
  });

  // sync state when the search parameter changes
  useEffect(() => {
    // get the current search param value
    const searchParam = searchParams.get(paramName);

    // if the search parameter is set, update state with the deserialized value
    if (searchParam !== null) {
      setState(deserialize(searchParam));
    } else {
      // fallback to the initial value
      setState(initialState);
    }
  }, [searchParams, deserialize, initialState]);

  // sync search parameter when state changes
  useEffect(() => {
    // serialize the new value
    const serialized = serialize(state);

    // get search params directly from the URL to avoid race conditions
    // const newSearchParams = new URLSearchParams(prevSearchParams);
    const newSearchParams = new URLSearchParams(window.location.search);

    // if the new value is empty, null, or undefined delete the search parameter
    if (serialized === '' || serialized === null || serialized === undefined) {
      newSearchParams.delete(paramName);
    } else {
      // set the search parameter value
      newSearchParams.set(paramName, serialized);
    }

    // return the updated URLSearchParams
    setSearchParams(newSearchParams, { replace: !keepHistory });
  }, [state]);

  return [state, setState];
}

export default useSearchParamState;
